import { Controller } from "@hotwired/stimulus"
import { parseDate, stdDateParseFormat, stdDateDisplayFormat } from "../../helpers/date_helpers"
import { DateTime } from 'luxon'

// Connects to data-controller="scenarios--range"
export default class extends Controller {
  static targets = [
    'dateMinDisplay', 'dateMinField',
    'dateMaxDisplay', 'dateMaxField',
    'period'
  ]

  static values = {
    dateMin: String,
    dateMax: String,
    period:  String,
    mode: { type: String, default: 'display' },
    role: { type: String, default: 'follower' }
  }

  static classes = ['display', 'custom']

  connect() {
    app.configRanges ||= []
    app.configRanges.push(this)

    this.switchToMode()
    this.validateRange(this.dateMaxFieldTarget)
  }

  periodValueChanged (val) {
    if (val) {
      this.periodTarget.value = val
    }
  }

  dateMinValueChanged(val, previousVal) {
    if (val) {
      this.dateMinDisplayTarget.textContent = this.dateMinDisplay
      this.syncFieldUnlessFocused(this.dateMinFieldTarget, val)
    }
  }

  dateMaxValueChanged(val, previousVal) {
    if (val) {
      this.dateMaxDisplayTarget.textContent = this.dateMaxDisplay
      this.syncFieldUnlessFocused(this.dateMaxFieldTarget, val)
    }
  }

  // Writing .value while the user is typing a year segment resets the native
  // picker to 0002 / 0020 / etc. Only push from Stimulus when they are done.
  syncFieldUnlessFocused(field, val) {
    if (document.activeElement === field) return

    field.value = val
  }

  minFieldChanged(event) {
    this.applyFieldChange(event.target, 'dateMin')
  }

  maxFieldChanged(event) {
    this.applyFieldChange(event.target, 'dateMax')
  }

  applyFieldChange(field, valueName) {
    this.clearRangeValidity()

    if (this.fieldMissingOrBad(field)) return

    this[`${valueName}Value`] = field.value
    this.validateRange(field)
  }

  fieldMissingOrBad(field) {
    return !field.value || field.validity?.valid === false ||
      field.validity?.valueMissing || field.validity?.badInput
  }

  clearRangeValidity() {
    this.dateMinFieldTarget.setCustomValidity('')
    this.dateMaxFieldTarget.setCustomValidity('')
  }

  // Keep custom errors silent while editing. reportValidity() steals the
  // date-segment caret and traps focus on the invalid field.
  validateRange(editedField) {
    const start = parseDate(this.dateMinFieldTarget.value)
    const end = parseDate(this.dateMaxFieldTarget.value)

    if (!start?.isValid || !end?.isValid) return

    if (end < start) {
      editedField.setCustomValidity('End date must be on or after start date')
    } else if (end > start.plus({ years: 1 })) {
      editedField.setCustomValidity('Range cannot be longer than 1 year')
    }
  }

  // Called from the parent form submit. Sets custom errors so native
  // checkValidity() can block the post even if change never fired.
  validateForSubmit() {
    this.clearRangeValidity()
    this.validateRange(this.dateMaxFieldTarget)

    const firstInvalid = [this.dateMinFieldTarget, this.dateMaxFieldTarget]
      .find((field) => !field.checkValidity())

    if (firstInvalid && typeof firstInvalid.reportValidity === 'function') {
      firstInvalid.reportValidity()
      return false
    }

    return true
  }

  modeValueChanged(val, prevVal) {
    this.switchToMode()
  }

  switchToMode() {
    if (this.modeValue == 'custom') {
      this.element.classList.remove(...this.displayClasses)
      this.element.classList.add(...this.customClasses)
    } else {
      this.element.classList.remove(...this.customClasses)
      this.element.classList.add(...this.displayClasses)
    }
  }

  toggleMode(event) {
    if (event?.detail?.mode) {
      this.modeValue = event.detail.mode === 'custom' ? 'custom' : 'display'
    }
  }

  get dateMin() {
    let dt = parseDate(this.dateMinValue)
    if (dt.invalid) { return DateTime.now().minus({ days: 30 }) }

    return dt
  }

  get dateMinStr() { return this.dateMin.toFormat(stdDateParseFormat) }
  get dateMinDisplay() { return this.dateMin.toFormat(stdDateDisplayFormat) }

  set dateMin(str) {
    let dt = parseDate(str)

    if (dt && dt.isValid) {
      this.dateMinValue = dt.toFormat(stdDateParseFormat)
    }
  }

  get dateMax() {
    let dt = parseDate(this.dateMaxValue)
    if (dt.invalid) { return DateTime.now() }

    return dt
  }

  get dateMaxStr() { return this.dateMax.toFormat(stdDateParseFormat) }
  get dateMaxDisplay() { return this.dateMax.toFormat(stdDateDisplayFormat) }

  set dateMax(str) {
    let dt = parseDate(str)
    this.dateMaxValue = dt.toFormat(stdDateParseFormat)
  }
}
